import { Category } from "../components/CategoryMegaMenu";
import { findCategoryPath } from "./categoryUtils";

export type IndustryOption = {
  id: string;
  name: string;
};

type CategoryTreeNode = {
  id: string;
  name: string;
  parentId?: string | null;
  children?: CategoryTreeNode[];
};

/** Root-level industries (Level 1 / Industry) from a hierarchy API response. */
export function extractRootIndustryOptions(
  categories: unknown[],
): IndustryOption[] {
  if (!Array.isArray(categories)) return [];
  return categories
    .filter((c): c is CategoryTreeNode => {
      const node = c as CategoryTreeNode;
      return Boolean(node?.id && node?.name) && !node.parentId;
    })
    .map((r) => ({ id: String(r.id), name: String(r.name) }));
}

/**
 * Restrict product category tree to seller's selected industries (root category IDs).
 * Supports legacy stored Level-2 IDs by keeping only matching children under their root.
 */
export function applySellerIndustryCategoryFilter<
  T extends CategoryTreeNode,
>(categories: T[], allowedIndustryIds: string[]): T[] {
  const allowed = Array.isArray(allowedIndustryIds) ? allowedIndustryIds : [];
  if (allowed.length === 0) return [];

  const allowedSet = new Set(allowed.map(String));
  const roots = (categories || []).filter((c) => !c.parentId);

  return roots
    .map((root) => {
      if (allowedSet.has(String(root.id))) {
        return root;
      }

      const children = (root.children || []).filter((ch) =>
        allowedSet.has(String(ch.id)),
      );
      if (children.length > 0) {
        return { ...root, children };
      }

      return null;
    })
    .filter((r): r is T => r != null);
}

/** Resolve display label for a stored industry id (root or legacy Level-2). */
export function resolveIndustryDisplayName(
  idOrName: string,
  rootOptions: IndustryOption[],
  categoryTree?: Category[],
): string {
  const trimmed = String(idOrName || "").trim();
  if (!trimmed) return "";

  const direct = rootOptions.find(
    (c) => c.id === trimmed || c.name === trimmed,
  );
  if (direct) return direct.name;

  if (categoryTree?.length) {
    const path = findCategoryPath(categoryTree, trimmed);
    if (path?.length) {
      return path[0].name;
    }
  }

  return trimmed;
}

/** Unique root industries from stored ids (root or legacy Level-2). */
export function resolveSelectedIndustries(
  allowedIndustryIds: string[],
  rootOptions: IndustryOption[],
  categoryTree?: Category[],
): IndustryOption[] {
  const seenRootIds = new Set<string>();
  const result: IndustryOption[] = [];

  for (const id of allowedIndustryIds) {
    const key = String(id).trim();
    if (!key) continue;

    const directRoot = rootOptions.find((r) => r.id === key);
    let rootId = directRoot?.id;
    let rootName = directRoot?.name;

    if (!rootId && categoryTree?.length) {
      const path = findCategoryPath(categoryTree, key);
      if (path?.[0]) {
        rootId = path[0].id;
        rootName = path[0].name;
      }
    }

    if (!rootId || !rootName || seenRootIds.has(rootId)) continue;
    seenRootIds.add(rootId);
    result.push({ id: rootId, name: rootName });
  }

  return result;
}
